/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from '@/assets/images/logo.png';
import heroAircraft from '@/assets/images/hero-aircraft.jpg';
import btnExperimental from '@/assets/images/btn-experimental.jpg';
import btnCertified from '@/assets/images/btn-certified.jpg';
import btnParts from '@/assets/images/btn-parts.jpg';

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo image with multiple path mappings
  '/wp-content/themes/barrett/images/logo.png': logo,
  '/images/logo.png': logo,
  '/assets/images/logo.png': logo,
  
  // Hero section images
  '/wp-content/uploads/2014/03/slide1.jpg': heroAircraft,
  '/images/hero-aircraft.jpg': heroAircraft,
  '/assets/images/hero-aircraft.jpg': heroAircraft,
  
  // Button images for sidebar
  '/wp-content/themes/barrett/images/btn-index-1.jpg': btnExperimental,
  '/images/btn-experimental.jpg': btnExperimental,
  '/assets/images/btn-experimental.jpg': btnExperimental,
  
  '/wp-content/themes/barrett/images/btn-index-2.jpg': btnCertified,
  '/images/btn-certified.jpg': btnCertified,
  '/assets/images/btn-certified.jpg': btnCertified,
  
  '/wp-content/themes/barrett/images/btn-index-3.jpg': btnParts,
  '/images/btn-parts.jpg': btnParts,
  '/assets/images/btn-parts.jpg': btnParts,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
